import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { SERVICES, CATEGORIES, COMPANY } from "../data/mock";

function PageHero({ eyebrow, title, subtitle }) {
  return (
    <section className="bg-[var(--brand-hero)]">
      <div className="mx-auto max-w-4xl px-5 py-16 text-center lg:py-20 lg:px-8">
        <span className="text-sm font-bold uppercase tracking-wider text-teal-brand">{eyebrow}</span>
        <h1 className="mt-3 text-4xl font-extrabold text-navy-brand sm:text-5xl">{title}</h1>
        <p className="mx-auto mt-5 max-w-2xl text-[15px] leading-relaxed text-slate-500">{subtitle}</p>
      </div>
    </section>
  );
}

export default function Services() {
  return (
    <main>
      <PageHero
        eyebrow="Our Services"
        title="Inventory solutions, end to end"
        subtitle="We buy consumer-goods inventory of nearly every category and manage the entire liquidation cycle — so you recover capital quickly, without disrupting your marketplace."
      />

      <div className="mx-auto max-w-7xl px-5 py-16 lg:px-8">
        <div className="space-y-20">
          {SERVICES.map((s, idx) => {
            const Icon = s.icon;
            const reverse = idx % 2 === 1;
            return (
              <div
                key={s.id}
                id={s.id}
                className={`grid items-center gap-10 lg:grid-cols-2 ${reverse ? "lg:[&>*:first-child]:order-2" : ""}`}
              >
                <div className="overflow-hidden rounded-3xl shadow-xl">
                  <img src={s.image} alt={s.title} className="h-full max-h-[400px] w-full object-cover" />
                </div>
                <div>
                  <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-teal-brand text-white">
                    <Icon size={26} />
                  </div>
                  <h2 className="text-2xl font-extrabold text-navy-brand sm:text-3xl">{s.title}</h2>
                  <p className="mt-4 text-[15px] leading-relaxed text-slate-500">{s.long}</p>
                  <Link
                    to="/contact"
                    className="mt-7 inline-flex items-center gap-2 rounded-full bg-navy-brand px-7 py-3.5 text-sm font-semibold text-white transition-all duration-300 hover:bg-teal-brand"
                  >
                    Request a Quote <ArrowRight size={17} />
                  </Link>
                </div>
              </div>
            );
          })}
        </div>

        {/* Categories */}
        <div className="mt-20 rounded-3xl bg-slate-50 p-8 lg:p-12">
          <h2 className="text-center text-2xl font-extrabold text-navy-brand sm:text-3xl">
            Categories we purchase
          </h2>
          <p className="mx-auto mt-3 max-w-2xl text-center text-sm text-slate-500">
            From apparel to automotive — basically anything you would find in a typical big-box super center.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            {CATEGORIES.map((c) => (
              <span
                key={c}
                className="rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-navy-brand transition-all duration-300 hover:border-teal-brand hover:bg-teal-brand hover:text-white"
              >
                {c}
              </span>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="mt-16 rounded-3xl bg-navy-brand px-6 py-12 text-center text-white lg:px-12">
          <h2 className="text-2xl font-extrabold sm:text-3xl">Have inventory to move?</h2>
          <p className="mx-auto mt-3 max-w-xl text-sm text-slate-300">
            Send us your inventory list and one of our buyers will follow up with a no-obligation offer.
          </p>
          <div className="mt-7 flex flex-wrap justify-center gap-4">
            <Link to="/contact" className="rounded-full bg-teal-brand px-7 py-3.5 text-sm font-semibold text-white transition-transform hover:scale-105">
              Get a Free Consultation
            </Link>
            <a href={COMPANY.phoneHref} className="rounded-full border border-white/25 px-7 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-white/10">
              Call {COMPANY.phone}
            </a>
          </div>
        </div>
      </div>
    </main>
  );
}
